<template>
  <div>
    <Vuedraggable
      :list="groupedList"
      class="tour-day-item-container"
      draggable=".item-container:not(.not-draggable)"
      item-key="id"
      v-bind="dragOptions"
      @change="onChanged"
    >
      <template #item="{ element }">
        <div
          v-if="!!element.hotel"
          :class="{
            'not-draggable ghost-item': element.disabled || props.updatingTour,
            'not-draggable blocked-item': element.isBlocked,
            'item-selected': showHotelDropdown,
          }"
          class="item-container position-relative"
        >
          <div class="tour-day-item-actions d-flex justify-content-center dropup">
            <v-button
              :disabled="props.updatingTour"
              class="p-4 dropdown-toggle"
              size="sm"
              variant="icon"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              @click="toggleHotelList(!showHotelDropdown)"
            >
              <v-icon icon="group" title="Click to see all suppliers" />
            </v-button>
            <ul class="dropdown-menu pt-0 custom-scroll">
              <li class="dropdown-header p-8 d-flex justify-content-end">
                <div class="custom-tooltip">
                  <v-button class="btn-icon p-4" size="xs" variant="outline-secondary" @click="addHotel">
                    <v-icon icon="plus" />
                  </v-button>
                  <span class="custom-tooltip-text">
                    {{  t("tourBuilder.addHotel") }}
                    <div class="right-arrow"></div>
                  </span>
                </div>
              </li>
              <Vuedraggable
                :list="element.hotel"
                class="hotel-list"
                draggable=".hotel-row"
                item-key="id"
                v-bind="hotelDragOptions"
                @change="onHotelChanged"
                              >
                  <template #item="{ element: hotel }">
                    <li
                    :key="hotel.id"
                    class="d-flex justify-content-between align-items-center border-bottom py-2 dropdown-item hotel-row"
                  >
                    <div class="d-flex align-items-center gap-2">
                      <div 
                        class="drag-handle text-muted cursor-grab"
                        title="Drag to reorder"
                      >
                        <div class="drag-lines">
                          <div class="drag-line"></div>
                          <div class="drag-line"></div>
                          <div class="drag-line"></div>
                        </div>
                      </div>
                      <div class="text-sm">{{ hotel.supplier ? hotel.supplier : t("tourBuilder.selectHotel") }}</div>
                    </div>

                    <div class="d-flex align-items-center gap-2 hotel-actions">
                      <v-button
                        :disabled="props.updatingTour"
                        class="p-4"
                        size="sm"
                        variant="icon"
                        @click="
                          setCurrentItem(hotel);
                          toggleHotelList(false);
                        "
                      >
                        <v-icon icon="pencil" />
                      </v-button>

                      <v-button
                        :disabled="props.updatingTour"
                        class="p-4"
                        size="sm"
                        variant="icon"
                        @click="
                          deleteItem(hotel, hotel.supplier);
                          toggleHotelList(false);
                        "
                      >
                        <v-icon icon="trash" />
                      </v-button>
                    </div>
                  </li>
                </template>
              </Vuedraggable>
            </ul>
          </div>

          <div class="d-flex justify-content-center">
            <div
              :class="{
                'not-draggable ghost-hotel-item': element.disabled,
                'blocked-item': element.isBlocked,
              }"
              class="tour-day-item d-flex align-items-center justify-content-center position-relative"
            >
              <span
                class="position-absolute top-0 end-0 bg-primary text-white rounded-circle text-xs d-flex align-items-center justify-content-center"
                style="width: 20px; height: 20px; transform: translate(50%, -50%); z-index: 1"
              >
                {{ element.hotel.length }}
              </span>
              <v-icon :icon="searchItem(element.type)?.icon || 'question'" class="text-xxl" />
            </div>
          </div>

          <div class="supplier-name text-xs text-center fw-light mt-8" :title="element.supplier">
            <template v-if="element.type === 'stage_information'">
              {{ element?.resources[0]?.resource?.title }}
            </template>
            <template v-else-if="element.type === 'general_information'">
              {{ element?.resources[0]?.resource?.title }}
            </template>
            <template v-else>
              {{ element.supplier }}
            </template>
          </div>
        </div>
        <div
          v-else
          :class="{
            'not-draggable ghost-item': element.disabled || props.updatingTour,
            'not-draggable blocked-item': element.isBlocked,
            'item-selected': element.id === userStore.currentTourDayItem?.id,
          }"
          class="item-container position-relative"
        >
          <div class="tour-day-item-actions d-flex justify-content-center">
            <v-button
              :disabled="props.updatingTour"
              class="p-4"
              size="sm"
              variant="icon"
              @click="setCurrentItem(element)"
            >
              <v-icon icon="pencil" />
            </v-button>

            <v-button
              :disabled="props.updatingTour"
              class="p-4"
              size="sm"
              variant="icon"
              @click="deleteItem(element, element.supplier)"
            >
              <v-icon icon="trash" />
            </v-button>
          </div>

          <div class="d-flex justify-content-center">
            <div
              :class="{
                'not-draggable ghost-hotel-item': element.disabled,
                'blocked-item': element.isBlocked,
              }"
              class="tour-day-item d-flex align-items-center justify-content-center position-relative"
            >
              <v-icon :icon="searchItem(element.type)?.icon || 'question'" class="text-xxl" />
            </div>
          </div>

          <div class="supplier-name text-xs text-center fw-light mt-8" :title="element.supplier">
            <template v-if="element.type === 'stage_information'">
              {{ element?.resources[0]?.resource?.title }}
            </template>
            <template v-else-if="element.type === 'general_information'">
              {{ element?.resources[0]?.resource?.title }}
            </template>
            <template v-else>
              {{ element.supplier }}
            </template>
          </div>
        </div>
      </template>

      <template #footer>
        <v-button
          :disabled="props.updatingTour || !props.canAddItem"
          class="btn-add-item btn-icon p-4"
          size="sm"
          variant="outline-secondary"
          @click="openModal('the-template-day-item-create-modal')"
        >
          <v-icon icon="plus" />
        </v-button>
      </template>
    </Vuedraggable>

    <the-template-day-item-create-modal-v2
      v-if="modalToShow === 'the-template-day-item-create-modal'"
      :agency="props.agency"
      :template-id="props.templateId"
      :tour-day-id="props.templateDayId"
      @closed="closeModal"
      @emit-reload-tour="emitReloadTour"
      @open-details="setCurrentItem"
    />
  </div>
</template>

<script lang="ts" setup>
import { getCurrentInstance, PropType, ref } from "vue";
import Vuedraggable from "vuedraggable";
import { TYPE, useToast } from "vue-toastification";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import useModalUtils from "@/helpers/ModalUtils";
import { useUserStore } from "@/stores/user";
import { findConceptByKey, mapConcepts } from "@/helpers/ConceptHelper";
import useConcepts from "@/helpers/Concepts";
import TheTemplateDayItemCreateModalV2 from "@/components/template/V2/TheTemplateDayItemCreateModalV2.vue";
import api from "@/api";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
  list: {
    type: Array as PropType<Record<string, unknown>[]>,
    required: false,
  },
  templateDayId: {
    type: Number,
    required: true,
  },
  type: {
    type: String,
    required: false,
    default: "",
  },
  templateId: {
    type: Number,
    required: true,
  },
  agency: {
    type: String,
    required: true,
  },
  updatingTour: {
    type: Boolean,
    required: false,
    default: false,
  },
  canAddItem: {
    type: Boolean,
    required: false,
    default: true,
  },
  tourMapped: {
    type: Object,
    required: true,
  },
  originId: {
    type: Number,
    required: false,
    default: null,
  },
  destinationId: {
    type: Number,
    required: false,
    default: null,
  },
});

const emit = defineEmits(["emitReloadTour", "openItemDetailsOffCanvas", "moveItemToAnotherDay"]);

const { openModal, closeModal, modalToShow } = useModalUtils();
const concepts = useConcepts();
const itemTypes = computed(() => {
  return concepts.concepts.value?.["supplier.type"] ? mapConcepts(concepts.concepts.value["supplier.type"]) : [];
});

const dragOptions = computed(() => ({
  animation: 0,
  group: "tour-day-item",
  disabled: false,
  ghostClass: "ghost",
  preventOnFilter: false,
}));

const hotelDragOptions = computed(() => ({
  animation: 150,
  group: "hotel-list",
  disabled: props.updatingTour,
  ghostClass: "hotel-ghost",
  preventOnFilter: false,
}));
const showHotelDropdown = ref<boolean>(false);
const { t } = useI18n();
const toast = useToast();
const userStore = useUserStore();
const instance = getCurrentInstance();
const $modal = instance?.appContext.config.globalProperties.$modal;
const $toast = instance?.appContext.config.globalProperties.$toast;

const groupedList = computed(() => {
  if (!props.list) return [];

  const hotels = props.list.filter((item: any) => item.type === "hotel");
  const others = props.list.filter((item: any) => item.type !== "hotel");

  let groupedHotels: any[] = [];

  if (hotels.length > 1) {
    // Sort hotels by priorityOrder
    const sortedHotels = hotels.sort((a: any, b: any) => {
      const priorityA = a.priorityOrder || a.position || 0;
      const priorityB = b.priorityOrder || b.position || 0;
      return priorityA - priorityB;
    });
    
    // Group hotels if more than one
    groupedHotels.push({
      id: sortedHotels.map((hotel) => hotel.id).join("-"),
      hotel: sortedHotels,
      disabled: sortedHotels.some((hotel) => hotel.disabled),
      type: "hotel",
      supplier: "Multiple Hotels",
    });
  } else if (hotels.length === 1) {
    // If only 1 hotel, treat it normally (no grouping)
    groupedHotels = hotels;
  }

  return [...groupedHotels, ...others];
});

const toggleHotelList = (value: boolean) => {
  showHotelDropdown.value = value;
};

const onChanged = ({ moved, added }: any) => {
  if (moved) {
    const templateDay = props.tourMapped?.tourDays?.find((tourDay: any) => tourDay.id === moved.element.activityDayId);

    if (templateDay) {
      const itemReplaced = templateDay.items[moved.newIndex];
      moveItemPosition(moved.element, itemReplaced.position);
    }
  }

  if (added) {
    const dayItemsWithNewItem = props.list?.filter((item: any) => item.disabled || item.id !== added.element.id);

    if (dayItemsWithNewItem?.length) {
      const itemReplaced = dayItemsWithNewItem[added.newIndex];

      const data = {
        element: added.element,
        newPosition: itemReplaced ? (itemReplaced as any).position : (dayItemsWithNewItem.at(-1) as any)?.position + 1 || 1,
      };

      emit("moveItemToAnotherDay", data);
    }
  }
};

const onHotelChanged = ({ moved }: any) => {
  if (moved) {
    // Update hotel positions when reordered
    updateHotelPositions(moved.element, moved.newIndex);
  }
};

const updateHotelPositions = async (movedHotel: Record<string, any>, newIndex: number) => {
  try {
    const reqParam: Record<string, any> = {
      model_type: movedHotel.modelType,
      model_id: movedHotel.modelId,
      priority_order: newIndex + 1,
    };

    await api.activityService.updateDayItem(
      props.agency, 
      props.templateId, 
      (movedHotel as any).activityDayId || movedHotel.tourDayId, 
      movedHotel.id, 
      reqParam
    );

    toast.success(t("general.shared.savedChanges"), {
      position: "bottom-right" as any,
    });
    emit("emitReloadTour");
  } catch (e: any) {
    toast.error(e.response?.data?.message || t("tourBuilder.hotelReorderError"), {
      position: "bottom-right" as any,
    });
    emit("emitReloadTour");
  }
};

const moveItemPosition = async (item: Record<string, any>, newPosition: number) => {
  try {
    const reqParam: Record<string, any> = {
      model_type: item.modelType,
      model_id: item.modelId,
      position: newPosition,
      priority_order: newPosition,
    };

    await api.activityService.updateDayItem(props.agency, props.templateId, item.activityDayId, item.id, reqParam);

    toast.success(t("general.shared.savedChanges"), {
      position: "bottom-right" as any,
    });
    emit("emitReloadTour");
  } catch (e: any) {
    toast.error(e.response.data.message, {
      position: "bottom-right" as any,
    });
    emit("emitReloadTour");
  }
};

const setCurrentItem = (item: any) => {
  const data = {
    agency: props.agency,
    templateId: props.templateId,
    templateDayId: item.tourDayId ?? item.activityDayId,
    templateDayItemId: item.id,
    locationId: props.originId ?? props.destinationId,
  };
  emit("openItemDetailsOffCanvas", data);
};

const searchItem = (type: any) => {
  return itemTypes.value.find((itemType) => itemType.key === type);
};

const deleteItem = async (templateDayItem: Record<string, any>, name: null | string = null) => {
  if ($modal) {
    await $modal.delete({
      title: t("tourBuilder.deleteTourDayItemTitle"),
      text: t("tourBuilder.deleteTourDayItemText", {
        dayItem: name ?? (findConceptByKey("supplier.type", templateDayItem.type) as any)?.name,
      }),
      deleteButtonText: t("general.button.delete"),
      rightButtonClasses: "btn-tertiary",
      deleteButtonCallback: () => doDeleteItem(templateDayItem.id),
    });
  }
};

const doDeleteItem = async (templateDayItem: number) => {
  if (!$toast) return;
  const loadingToast = $toast.loading(t("general.button.deleting"), "toast-remove-template-day-item");

  try {
    await api.activityService.deleteActivityDayItem(
      props.agency,
      props.templateId,
      props.templateDayId,
      templateDayItem
    );

    emit("emitReloadTour");

    loadingToast.update("toast-remove-template-day-item", {
      content: t("tourBuilder.dayItemDeleted"),
      options: {
        type: TYPE.SUCCESS,
        icon: "icon icon-check-circle-fill",
        timeout: 5000,
      },
    });
  } catch (e: any) {
    console.error(e.response?.data);

    loadingToast.update("toast-remove-template-day-item", {
      content: t("tourBuilder.dayItemDeleteError"),
      options: {
        type: TYPE.ERROR,
        icon: "icon icon-exclamation-triangle-fill",
        timeout: 5000,
      },
    });

    toast.error(e.response?.data?.message || t("tourBuilder.dayItemDeleteError"), {
      position: "bottom-right" as any,
    });
  }
};

const addHotel = async () => {
  if (!$toast) return;
  const loadingToast = $toast.loading(t("general.button.adding"), "toast-add-template-day-item");
  try {
    const response = await api.activityService.createDayItem(props.agency, props.templateId, props.templateDayId, {
      type: "hotel",
    });

    const data = {
      id: response.id,
      tourDayId: (response as any).activityDayId || response.tourDayId,
    };
    setCurrentItem(data);
    emit("emitReloadTour");

    loadingToast.update("toast-add-template-day-item", {
      content: t("tourBuilder.dayItemCreated"),
      options: {
        type: TYPE.SUCCESS,
        icon: "icon icon-check-circle-fill",
        timeout: 5000,
      },
    });
  } catch (e: any) {
    loadingToast.update("toast-add-template-day-item", {
      content: t("tourBuilder.dayITemCreateError"),
      options: {
        type: TYPE.ERROR,
        icon: "icon icon-exclamation-triangle-fill",
        timeout: 5000,
      },
    });

    console.error(e.response?.data);
    toast.error(e.response?.data?.message, {
      position: "bottom-right" as any,
      timeout: 5000,
    });
  }
};

const emitReloadTour = () => {
  emit("emitReloadTour");
};
</script>

<style lang="scss" scoped>
.flip-list-move {
  transition: transform 0.5s;
}

.no-move {
  transition: transform 0s;
}

.list-group {
  min-height: 20px;
}

.list-group-item {
  cursor: move;
}

.list-group-item i {
  cursor: pointer;
}

.supplier-name {
  width: 4.25rem;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.list-enter-from {
  opacity: 0;
  transform: scaleY(0.25);
}

.list-enter-active,
.list-leave-active {
  transition: all 1s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-leave-to {
  opacity: 0;
  transform: scaleY(0);
}

.list-move {
  transition: all 1s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-leave-active {
  position: absolute;
}

.list-group-item {
  display: block;
  padding: 0;
  border-left: none;
  border-right: none;
  border-top: none;
  border-radius: 0;
  width: 100%;
}
.tour-day-item-container {
  padding-bottom: 65px;
}

.custom-scroll {
  max-height: 200px; 
  overflow-y: auto;
  overflow-x: hidden;
}

::-webkit-scrollbar {
  width: 8px;
  background-color: #d5d5d5;
  cursor: auto;
}

::-webkit-scrollbar-thumb {
  cursor: auto;
  background: #d7df23;
  border-radius: 10px;
}

.custom-tooltip {
  position: relative;
  display: inline-block;
}

.custom-tooltip-text {
  visibility: hidden;
  opacity: 0;
  background-color: #ee2a7b;
  color: #fff;
  text-align: center;
  border-radius: 4px;
  padding: 4px 8px;
  position: absolute;
  z-index: 99999999;
  top: 45%;
  right: 125%;
  transform: translateY(-50%);
  white-space: nowrap;
  transition: opacity 0.2s;
  font-size: 12px;
}

.right-arrow {
  position: absolute;
  top: 50%;
  right: -6px;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  border-left: 6px solid #ee2a7b; /* arrow points left */
}

.custom-tooltip:hover .custom-tooltip-text {
  visibility: visible;
  opacity: 1;
}
.hotel-actions {
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
}
.hotel-row:hover .hotel-actions {
  opacity: 1;
  pointer-events: auto;
}

.drag-handle {
  cursor: grab;
  opacity: 0;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
}

.drag-handle:hover {
  opacity: 1;
}

.hotel-row:hover .drag-handle {
  opacity: 1;
}

.drag-lines {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 2px;
}

.drag-line {
  width: 12px;
  height: 2px;
  background-color: #6c757d;
  border-radius: 1px;
}

.hotel-ghost {
  opacity: 0.5;
  background: #f8f9fa;
  border: 2px dashed #dee2e6;
}

.hotel-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.cursor-grab {
  cursor: grab;
}

.cursor-grab:active {
  cursor: grabbing;
}
</style>
